
import React from "react";
import { Schemes } from "../../../../utils";
import { i18nManager } from "../../../../i18n";
import { Elements } from "../../../shared/Elements";
import MediaInput from "../../../shared/MediaInput";
import StatefulLink from "../../../shared/StatefulLink";
import { ConfirmDialog, Siren } from "../../../shared/ConfirmDialog";
import LoadingGif from "../../../../assets/images/loading_circle.gif";
import { Button, ButtonManageRef, Checkbox, Form } from "@rivtn/noseur";
import { BaseService, ConsusService, HostService, IHost, IHostWebsite, IMember, IProfileWebsite, ITeam, IUploadedFile, MemberService, SanitizedResponse } from "../../../../services";

export interface HomeProps {
    host: IHost;
    profile: IMember;
    hostWebsite: IHostWebsite;
}

function Home(props: HomeProps) {

    const { host } = props;
    const hostService = HostService.getInstance();
    const consusService = ConsusService.getInstance();
    const labels = i18nManager.Labels.host.settings.home;
    const [profile, _] = React.useState<IMember>(props.profile);
    const [hostWebsite, setHostWebsite] = React.useState<IHostWebsite>(props.hostWebsite);
    const maxSponsors = 5; // TODO FETCH FROM PLAN

    return (<Form scheme={Schemes.OMNEHOST} className="profile-settings team-settings">
        <Checkbox label={labels.enable_ads} defaultChecked={hostWebsite?.home_settings?.enable_rivtn_ad} onChecked={(checked) => {
            setHostWebsite({ ...hostWebsite, home_settings: { ...hostWebsite.home_settings, enable_rivtn_ad: checked } });
        }} />
        <hr />
        <Checkbox label={labels.show_twitter_x} defaultChecked={hostWebsite?.home_settings?.show_twitter_feeds} onChecked={(checked) => {
            setHostWebsite({ ...hostWebsite, home_settings: { ...hostWebsite.home_settings, show_twitter_feeds: checked } });
        }} />
        <Checkbox label={labels.show_twitch_stream} defaultChecked={hostWebsite?.home_settings?.show_twitch_streams} onChecked={(checked) => {
            setHostWebsite({ ...hostWebsite, home_settings: { ...hostWebsite.home_settings, show_twitch_streams: checked } });
        }} />
        <Checkbox label={labels.show_insta_feed} defaultChecked={hostWebsite?.home_settings?.show_instagram_feeds} onChecked={(checked) => {
            setHostWebsite({ ...hostWebsite, home_settings: { ...hostWebsite.home_settings, show_instagram_feeds: checked } });
        }} />
        <Checkbox label={labels.show_top_tours} defaultChecked={hostWebsite?.home_settings?.show_top_tournaments} onChecked={(checked) => {
            setHostWebsite({ ...hostWebsite, home_settings: { ...hostWebsite.home_settings, show_top_tournaments: checked } });
        }} />
        <Checkbox label={labels.show_sponsor} defaultChecked={hostWebsite?.home_settings?.show_sponsors} onChecked={(checked) => {
            setHostWebsite({ ...hostWebsite, home_settings: { ...hostWebsite.home_settings, show_sponsors: checked } });
        }} />
        <Checkbox label={labels.show_top_best_tours_reviews} defaultChecked={hostWebsite?.home_settings?.show_top_best_tournament_reviews} onChecked={(checked) => {
            setHostWebsite({ ...hostWebsite, home_settings: { ...hostWebsite.home_settings, show_top_best_tournament_reviews: checked } });
        }} />
        <hr />
        <div className="sponsors-logos">
            <span>{labels.upload_host_sponsors}</span>
            <div className="logos">
                {hostWebsite.sponsors?.length < maxSponsors && (<i className="fa fa-plus" onClick={uploadSponsorLogo} />)}
                {hostWebsite.sponsors?.map((sponsor) => (<div key={sponsor}>
                    <img src={sponsor} alt="sponsor-logo" />
                    <StatefulLink style={{ marginTop: 20 }} label={<i className="fa fa-times" />} onClick={(done) => {
                        deleteSponsorLogo(sponsor, done);
                    }} />
                </div>))}
            </div>
        </div>
        <hr style={{ marginTop: 0 }} />
        <Button scheme={Schemes.OMNEHOST} text={i18nManager.Labels.common.save_changes} loadingProps={Elements.BUTTON_LOADING_PROPS}
            style={{ marginTop: 20 }} onClick={(e) => {
                e.manageRef?.setLoadingState(true);
                updateHostWebsite(() => e.manageRef?.setLoadingState(false));
            }} />
    </Form>);

    function updateHostWebsite(onDone?: () => void) {
        hostService.updateHostWebsite(host.external_id, hostWebsite!).then(({ sanitized }: SanitizedResponse<IHostWebsite>) => {
            setHostWebsite(sanitized);
            Siren.alert(labels.update_successful);
        }).catch(BaseService.reportError).finally(onDone);
    }

    function uploadSponsorLogo() {
        const dialogErrorRef = { current: null } as any;
        const mediaInputManageRef = { current: undefined as any };
        const dialogLabels = i18nManager.Labels.account.profile_website.home.dialog;

        const dialog = ConfirmDialog({
            closable: true,
            title: labels.upload_host_sponsor_logo,
            style: { minWidth: 400, maxWidth: "80%" },
            confirmLabel: i18nManager.Labels.common.upload,
            content: (<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "30px 50px" }}>
                <MediaInput loading noControl manageRef={mediaInputManageRef} width={"100%"} height={"100%"} maxFileSize={20000000}
                    style={{ alignSelf: "center", borderWidth: 0, boxShadow: "none", marginTop: 10 }}
                    itemTemplate={(options) => {
                        return (<div className="sponsor-logo-preview" key={options.index}>
                            <div>
                                <span>{dialogLabels.small_preview}</span>
                                {options.index === -1 && (<div className="sp">{(options.file as any)}</div>)}
                                {options.index > -1 && (<img className="sp" alt="small preview" src={options.url} />)}
                            </div>
                            <div>
                                <span>{dialogLabels.medium_preview}</span>
                                {options.index === -1 && (<div className="mp">{(options.file as any)}</div>)}
                                {options.index > -1 && (<img className="mp" alt="small preview" src={options.url} />)}
                            </div>
                            <div>
                                <span>{dialogLabels.large_preview}</span>
                                {options.index === -1 && (<div className="lp">{(options.file as any)}</div>)}
                                {options.index > -1 && (<img className="lp" alt="small preview" src={options.url} />)}
                            </div>
                        </div>);
                    }}
                    onValidationFail={(_: File, error: string) => {
                        dialogErrorRef.current!.innerText = error;
                        return true;
                    }}
                    onGenerateWithAi={async () => {
                        return new File(["We are here"], "we.txt", { type: 'text/plain' });
                    }} />
                <StatefulLink style={{ marginTop: 20 }} label={dialogLabels.select_another} onClick={(done) => {
                    dialogErrorRef.current!.innerText = " ";
                    mediaInputManageRef.current?.select();
                    done();
                }} />
                <div ref={dialogErrorRef} className="error" style={{ alignSelf: "flex-start" }}> </div>
            </div>),
            onConfirm: (button?: ButtonManageRef) => {
                dialogErrorRef.current!.innerText = " ";
                const files = mediaInputManageRef.current?.files() as File[];
                if (!files.length) {
                    dialogErrorRef.current!.innerText = dialogLabels.no_media_selected;
                    return false;
                }
                const file = files[0];
                button?.setLoadingState(true);
                consusService.uploadFile(`/omnehost/host/sponsor_logo/`, `${host.external_id}_${file.name.substring(file.name.length - 10, file.name.length)}`, file).then(({ sanitized }: SanitizedResponse<IUploadedFile>) => {
                    if (!hostWebsite?.sponsors) {
                        hostWebsite.sponsors = [];
                    }
                    hostWebsite?.sponsors.push(sanitized.consus_download_url);
                    dialog.hide();
                    setHostWebsite({ ...hostWebsite, sponsors: hostWebsite?.sponsors! });
                    updateHostWebsite();
                }).catch(BaseService.reportError).finally(() => {
                    button?.setLoadingState(false);
                });
                return false;
            }
        });
        dialog.show(() => mediaInputManageRef.current?.select());
    }

    function deleteSponsorLogo(url: string, done: () => void) {
        const removeMediaLink = () => {
            const sponsors = hostWebsite?.sponsors;
            sponsors?.splice(sponsors.indexOf(url), 1);
            setHostWebsite({ ...hostWebsite, sponsors });
            updateHostWebsite();
        };
        consusService.deleteFile([url]).then(removeMediaLink).catch((error: any) => {
            if (error.response.status === 404) {
                removeMediaLink();
                return;
            }
            BaseService.reportError(error);
        }).finally(done);
    }

}

export default Home;
